import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../lib/types';

/**
 * Loads and manages the user profile from the user_profiles table.
 *
 * Handles the "no profile yet" case gracefully: PGRST116 (row not found)
 * is treated as a valid state (profile === null) rather than an error,
 * since profiles are created lazily on first updateProfile() via upsert.
 */
export function useProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if profile has required name fields
  const hasName = profile?.first_name && profile?.last_name;

  const loadProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (fetchError) {
        // Profile doesn't exist yet - that's OK, we'll create it on first update
        if (fetchError.code === 'PGRST116') {
          setProfile(null);
        } else {
          throw fetchError;
        }
      } else {
        setProfile(data as UserProfile);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const updateProfile = async (updates: Partial<Pick<UserProfile, 'first_name' | 'last_name'>>) => {
    if (!userId) {
      return { error: 'Not authenticated' };
    }

    try {
      setError(null);

      // Upsert: create if doesn't exist, update if exists
      const { data, error: updateError } = await supabase
        .from('user_profiles')
        .upsert({
          id: userId,
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      setProfile(data as UserProfile);
      return { error: null };
    } catch (err) {
      console.error('Error updating profile:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMsg);
      return { error: errorMsg };
    }
  };

  return {
    profile,
    loading,
    error,
    hasName,
    loadProfile,
    updateProfile,
  };
}
